import { useParams, Link, Navigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowRight, Wrench, PhoneCall, Search, AlertCircle, ClipboardList } from "lucide-react";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Reveal, RevealGroup, revealItem } from "@/components/Reveal";
import { motion } from "framer-motion";
import Faq from "@/components/Faq";
import ProductRecommendation from "@/components/ProductRecommendation";
import AffiliateDisclosure from "@/components/AffiliateDisclosure";
import KidsPetsBadge from "@/components/KidsPetsBadge";
import ArticleCard from "@/components/ArticleCard";
import CTABlock from "@/components/CTABlock";
import { getPest, PESTS } from "@/data/pests";
import { getGuide } from "@/data/guides";

export default function PestPage() {
  const { slug } = useParams();
  const pest = getPest(slug);
  if (!pest) return <Navigate to="/pests" replace />;

  const Icon = Icons[pest.icon] || Icons.Bug;
  const related = (pest.relatedGuides || []).map(getGuide).filter(Boolean);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: `${pest.name}: DIY, pro help & what to know`,
    image: pest.heroImage,
    author: { "@type": "Organization", name: "DuckTheBugs" },
    publisher: { "@type": "Organization", name: "DuckTheBugs" },
    description: pest.intro,
  };

  const Section = ({ icon: SecIcon, eyebrow, title, children }) => (
    <Reveal>
      <div className="scroll-mt-24">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-forest/8 flex items-center justify-center">
            <SecIcon className="w-5 h-5 text-forest" />
          </div>
          <div>
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-terracotta">{eyebrow}</span>
            <h2 className="font-serif text-2xl sm:text-3xl font-bold text-forest-950 leading-tight">{title}</h2>
          </div>
        </div>
        {children}
      </div>
    </Reveal>
  );

  return (
    <div data-testid={`pest-page-${pest.slug}`}>
      <Seo title={`${pest.name}`} description={pest.intro} path={`/pests/${pest.slug}`} image={pest.heroImage} jsonLd={jsonLd} />

      {/* Hero */}
      <section className="relative pt-[72px] bg-forest-950 text-cream overflow-hidden">
        <div className="absolute inset-0 -z-0">
          <img src={pest.heroImage} alt={`${pest.name} in a natural setting`} className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-r from-forest-950 via-forest-950/85 to-forest-950/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 md:py-24">
          <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Pests", to: "/pests" }, { label: pest.name }]} />
          <div className="mt-6 flex items-center gap-3">
            <div className="w-14 h-14 rounded-2xl bg-cream/10 backdrop-blur flex items-center justify-center">
              <Icon className="w-7 h-7 text-duckgreen" />
            </div>
            <div className="flex gap-2 flex-wrap">
              <span className="px-3 py-1 rounded-full bg-cream/10 text-xs font-mono text-cream/90">{pest.diyFeasibility}</span>
              <span className="px-3 py-1 rounded-full bg-cream/10 text-xs font-mono text-cream/90">{pest.urgency}</span>
            </div>
          </div>
          <h1 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.02] max-w-3xl">
            {pest.tagline}
          </h1>
          <p className="mt-5 text-lg text-cream/85 max-w-2xl leading-relaxed">{pest.intro}</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <a href="#diy" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-duckgreen text-forest-950 font-bold hover:bg-duckgreen/90 transition-all active:scale-95" data-testid="pest-try-diy-btn">
              <Wrench className="w-4 h-4" /> Try DIY Solutions
            </a>
            <Link to={`/hire-a-pro/${pest.slug}`} className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-cream/10 border border-cream/30 text-cream font-semibold hover:bg-cream/20 transition-all active:scale-95" data-testid="pest-find-pro-btn">
              <PhoneCall className="w-4 h-4" /> Find a Pro
            </Link>
          </div>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-8 lg:px-12 py-16 md:py-24 space-y-16 md:space-y-20">
        {/* Identify */}
        <Section icon={Search} eyebrow="Step one" title="How to identify the problem">
          <ul className="space-y-3">
            {pest.identify.map((t, i) => (
              <li key={i} className="flex items-start gap-3 text-forest-950/80 text-[15px] leading-relaxed">
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-terracotta shrink-0" />
                {t}
              </li>
            ))}
          </ul>
        </Section>

        {/* Causes */}
        <Section icon={AlertCircle} eyebrow="The why" title="Common causes">
          <div className="grid sm:grid-cols-2 gap-4">
            {pest.causes.map((c, i) => (
              <div key={i} className="rounded-2xl bg-sand p-5">
                <h4 className="font-serif text-lg font-bold text-forest-950">{c.title}</h4>
                <p className="mt-1.5 text-sm text-forest-950/70 leading-relaxed">{c.body}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* DIY steps */}
        <div id="diy" className="scroll-mt-24">
          <Section icon={Wrench} eyebrow="Roll up your sleeves" title="Things you can do yourself">
            <RevealGroup className="space-y-3">
              {pest.diySteps.map((s, i) => (
                <motion.div key={i} variants={revealItem} className="flex gap-4 rounded-2xl border border-forest/10 bg-card p-5">
                  <span className="font-mono text-terracotta font-semibold shrink-0">{String(i + 1).padStart(2, "0")}</span>
                  <div>
                    <h4 className="font-serif text-lg font-bold text-forest-950">{s.title}</h4>
                    <p className="mt-1 text-sm text-forest-950/70 leading-relaxed">{s.body}</p>
                  </div>
                </motion.div>
              ))}
            </RevealGroup>
          </Section>
        </div>

        {/* Products */}
        <Section icon={ClipboardList} eyebrow="If you want tools" title="Products & tools that may help">
          <div className="mb-5">
            <AffiliateDisclosure />
          </div>
          <RevealGroup className="grid sm:grid-cols-2 gap-4">
            {pest.products.map((p) => (
              <motion.div key={p} variants={revealItem}>
                <ProductRecommendation slug={p} />
              </motion.div>
            ))}
          </RevealGroup>
        </Section>

        {/* When to call a pro */}
        <Reveal>
          <div className="rounded-3xl bg-forest-950 text-cream p-8 sm:p-10">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-terracotta/25 flex items-center justify-center">
                <PhoneCall className="w-5 h-5 text-terracotta" />
              </div>
              <h2 className="font-serif text-2xl sm:text-3xl font-bold text-cream">When to call a professional</h2>
            </div>
            <ul className="space-y-3 mb-7">
              {pest.whenToCallPro.map((t, i) => (
                <li key={i} className="flex items-start gap-3 text-cream/85 leading-relaxed">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-terracotta shrink-0" />
                  {t}
                </li>
              ))}
            </ul>
            <Link to={`/hire-a-pro/${pest.slug}`} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-duckgreen text-forest-950 font-bold hover:bg-duckgreen/90 transition-all" data-testid="pest-when-pro-cta">
              Find a Pro for {pest.name} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </Reveal>

        {/* Pro treatment */}
        <Section icon={ClipboardList} eyebrow="Know before you hire" title="What professional treatment usually involves">
          <ul className="space-y-3">
            {pest.proTreatment.map((t, i) => (
              <li key={i} className="flex items-start gap-3 text-forest-950/80 text-[15px] leading-relaxed">
                <span className="font-mono text-sm text-terracotta/70">{String(i + 1).padStart(2, "0")}</span>
                {t}
              </li>
            ))}
          </ul>
        </Section>

        {/* Kids & pets */}
        <Reveal>
          <KidsPetsBadge body={pest.kidsPets} />
        </Reveal>

        {/* FAQ */}
        <Faq items={pest.faqs} />
      </div>

      {/* Related articles */}
      {related.length > 0 && (
        <section className="bg-sand grain">
          <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 md:py-20">
            <Reveal>
              <h2 className="font-serif text-2xl sm:text-3xl font-bold text-forest-950 mb-8">Related reading</h2>
            </Reveal>
            <RevealGroup className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {related.map((g) => (
                <ArticleCard key={g.slug} guide={g} />
              ))}
            </RevealGroup>
          </div>
        </section>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 md:py-20">
        <CTABlock
          variant="amber"
          eyebrow="Still deciding?"
          title="DIY first? We'll show you where to start."
          body="Explore prevention, tools, and step-by-step help in the DIY hub — or get matched with a local pro when it's time."
          primary={{ to: "/diy", label: "Explore the DIY Hub" }}
          secondary={{ to: `/hire-a-pro/${pest.slug}`, label: "Find a Pro" }}
          showDuck
        />
      </div>
    </div>
  );
}
